import React from 'react';
import './roster-badge.scss';
import classNames from '../../lib/class-names';
import {safeConsoleError} from '../../lib/sanitize';
import {getRosterSlotClass, isValidRosterSlot, RosterSlot} from '../../lib/roster-colors';

export type {RosterSlot};

export type RosterBadgeSize = 'sm' | 'md' | 'lg';

export type RosterBadgeProps = {
    /**
     * Roster slot number (1-5)
     * Determines both the displayed numeral and the color fill
     */
    slot: RosterSlot;
    /**
     * Size variant
     * - sm: 16px × 16px
     * - md: 20px × 20px (default)
     * - lg: 24px × 24px
     */
    size?: RosterBadgeSize;
    /**
     * Adds the `roster-badge--bordered` class: a neutral outer ring around the
     * slot-coloured inset border (always drawn), for definition on light layers.
     */
    bordered?: boolean;
    /**
     * Additional CSS classes
     */
    className?: string;
    /**
     * ARIA label for accessibility
     * Defaults to "Roster {slot}"
     */
    ariaLabel?: string;
};

/**
 * RosterBadge — Square outlined badge displaying roster slot numeral (1-5)
 *
 * 20px × 20px badge (default): light cell, dark numeral, and a 1px edge in the
 * slot's colour. Outlined rather than filled so it never reads as a pass/fail dot.
 * Numeral is the primary identifier; color is reinforcement.
 *
 * Features:
 * - 5 distinct slot edge colors with measured contrast (≥4.5:1 against white)
 * - Size variants: sm (16px), md (20px), lg (24px)
 * - High contrast mode support
 *
 * Usage:
 * ```tsx
 * <RosterBadge slot={1} />
 * <RosterBadge slot={2} size="lg" bordered />
 * <RosterBadge slot={3} size="sm" ariaLabel="Comparison roster 3" />
 * ```
 *
 * Design Note:
 * Color binds to the slot number. Roster comparison numbers the selected
 * rosters 1..N in the order they were added and renumbers them when one is
 * removed, so the badges never have gaps (see RosterComparisonStore).
 */
export default function RosterBadge({
    slot,
    size = 'md',
    bordered = false,
    className,
    ariaLabel,
}: RosterBadgeProps) {
    // Validate slot is within range 1-5
    if (!isValidRosterSlot(slot)) {
        safeConsoleError(`RosterBadge: Invalid slot ${slot}. Must be 1-5.`);
        return null;
    }

    const classes = classNames(
        'roster-badge',
        getRosterSlotClass(slot),
        `roster-badge--${size}`,
        {
            'roster-badge--bordered': bordered,
        },
        className,
    );

    const label = ariaLabel || `Roster ${slot}`;

    return (
        <span
            className={classes}
            role='img'
            aria-label={label}
            data-testid={`roster-badge-${slot}`}
        >
            {slot}
        </span>
    );
}
